import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  MoreHorizontal,
  Building2 as BuildingIcon,
  Trash2,
  ShieldOff,
  Eye,
  Edit,
  Users,
  BookOpen,
  Download,
  UserCog,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DataTable } from "@/components/admin/DataTable";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import {
  getBuildings,
} from "@/lib/admin/queries";
import {
  updateBuilding,
  suspendBuilding,
  deleteBuilding,
  createBuilding,
  assignManager,
} from "@/lib/admin/actions";
import type { Organization } from "@/types/database";
import { useAuth } from "@/contexts/AuthContext";
import type { DataTableColumn } from "@/components/admin/DataTable";

const CITIES = [
  "Vancouver",
  "Calgary",
  "Edmonton",
  "Winnipeg",
  "Toronto",
  "Ottawa",
  "Montréal",
  "Halifax",
];

const ALL_VALUE = "__all__";

type ConfirmActionType =
  | { type: "suspend"; buildingId: string; name: string }
  | { type: "delete"; buildingId: string; name: string }
  | null;

type CreateFormValues = {
  name: string;
  address: string;
  city: string;
  plan: string;
  nightCap: number;
  adminId: string;
};

type EditFormValues = {
  name: string;
  address: string;
  nightCap: number;
};

function ComplianceBadge({ pct }: { pct: number }) {
  const colorClass =
    pct >= 80
      ? "text-green-700 bg-green-100"
      : pct >= 60
      ? "text-amber-700 bg-amber-100"
      : "text-red-700 bg-red-100";

  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums",
        colorClass
      )}
    >
      {pct}%
    </span>
  );
}

export default function Buildings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [city, setCity] = useState("");
  const [plan, setPlan] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<Organization | null>(null);
  const [assigningBuilding, setAssigningBuilding] = useState<Organization | null>(null);
  const [managerId, setManagerId] = useState("");
  const [confirmAction, setConfirmAction] = useState<ConfirmActionType>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const queryFilters = {
    search: debouncedSearch || undefined,
    city: city || undefined,
    plan: plan || undefined,
  };

  const buildingsQuery = useQuery({
    queryKey: ["admin", "buildings", queryFilters, page, pageSize],
    queryFn: () => getBuildings(queryFilters, page, pageSize),
  });

  const refetchBuildings = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "buildings"] });

  const suspendMutation = useMutation({
    mutationFn: (id: string) => suspendBuilding(id, actorId),
    onSuccess: () => { toast.success("Building suspended"); refetchBuildings(); },
    onError: () => toast.error("Failed to suspend building"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBuilding(id, actorId),
    onSuccess: () => { toast.success("Building deleted"); refetchBuildings(); },
    onError: () => toast.error("Failed to delete building"),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateFormValues) =>
      createBuilding(
        {
          name: data.name,
          address: data.address,
          city: data.city,
          adminId: data.adminId,
          nightCap: Number(data.nightCap),
          plan: data.plan,
        },
        actorId
      ),
    onSuccess: () => {
      toast.success("Building created");
      setShowCreateModal(false);
      createForm.reset();
      refetchBuildings();
    },
    onError: () => toast.error("Failed to create building"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Organization> }) =>
      updateBuilding(id, data, actorId),
    onSuccess: () => {
      toast.success("Building updated");
      setEditingBuilding(null);
      refetchBuildings();
    },
    onError: () => toast.error("Failed to update building"),
  });

  const assignManagerMutation = useMutation({
    mutationFn: ({ buildingId, mgrId }: { buildingId: string; mgrId: string }) =>
      assignManager(buildingId, mgrId, actorId),
    onSuccess: () => {
      toast.success("Manager assigned");
      setAssigningBuilding(null);
      setManagerId("");
    },
    onError: () => toast.error("Failed to assign manager"),
  });

  const createForm = useForm<CreateFormValues>({
    defaultValues: {
      name: "",
      address: "",
      city: "",
      plan: "strata",
      nightCap: 90,
      adminId: "",
    },
  });

  const editForm = useForm<EditFormValues>();
  useEffect(() => {
    if (editingBuilding) {
      editForm.reset({
        name: editingBuilding.name,
        address: editingBuilding.address ?? "",
        nightCap: 90,
      });
    }
  }, [editingBuilding, editForm]);

  async function handleConfirm() {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      if (confirmAction.type === "suspend") {
        await suspendMutation.mutateAsync(confirmAction.buildingId);
      } else if (confirmAction.type === "delete") {
        await deleteMutation.mutateAsync(confirmAction.buildingId);
      }
    } finally {
      setConfirmLoading(false);
      setConfirmAction(null);
    }
  }

  function exportCsv(building: Organization) {
    const row = `"${building.id}","${building.name}","${building.address ?? ""}","${building.plan}","${building.total_units}"`;
    const csv = `"ID","Name","Address","Plan","Units"\n${row}`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-${building.name.replace(/\s+/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const columns: DataTableColumn<Organization & Record<string, unknown>>[] = [
    {
      key: "id",
      label: "ID",
      render: (row) => (
        <span className="text-xs font-mono text-gray-400">{(row.id as string).slice(0, 8)}</span>
      ),
    },
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate max-w-[180px]">{row.name as string}</p>
          <p className="text-xs text-gray-400 truncate max-w-[180px]">{(row.address as string | null) ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "plan",
      label: "Plan",
      render: (row) => (
        <span className="text-xs font-medium capitalize whitespace-nowrap">
          {(row.plan as string).replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "total_units",
      label: "Units",
      render: (row) => (
        <span className="text-sm tabular-nums">{row.total_units as number}</span>
      ),
    },
    {
      key: "compliance",
      label: "Compliance",
      render: () => <ComplianceBadge pct={100} />,
    },
    {
      key: "created_at",
      label: "Created",
      render: (row) => (
        <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
          {new Date(row.created_at as string).toLocaleDateString("en-CA")}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const building = row as unknown as Organization;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open building actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem className="whitespace-nowrap" onClick={() => navigate(`/admin/buildings/${building.id}`)}>
                <Eye className="h-4 w-4 mr-2" /> View detail
              </DropdownMenuItem>
              <DropdownMenuItem className="whitespace-nowrap" onClick={() => setEditingBuilding(building)}>
                <Edit className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem className="whitespace-nowrap" onClick={() => setAssigningBuilding(building)}>
                <UserCog className="h-4 w-4 mr-2" /> Assign manager
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => navigate(`/admin/buildings/${building.id}?tab=units`)}
              >
                <Users className="h-4 w-4 mr-2" /> View units
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() => navigate(`/admin/buildings/${building.id}?tab=bookings`)}
              >
                <BookOpen className="h-4 w-4 mr-2" /> View bookings
              </DropdownMenuItem>
              <DropdownMenuItem className="whitespace-nowrap" onClick={() => exportCsv(building)}>
                <Download className="h-4 w-4 mr-2" /> Export compliance CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="whitespace-nowrap"
                onClick={() =>
                  setConfirmAction({ type: "suspend", buildingId: building.id, name: building.name })
                }
              >
                <ShieldOff className="h-4 w-4 mr-2" /> Suspend
              </DropdownMenuItem>
              <DropdownMenuItem
                className="whitespace-nowrap text-red-600 focus:text-red-700 focus:bg-red-50"
                onClick={() =>
                  setConfirmAction({ type: "delete", buildingId: building.id, name: building.name })
                }
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const filterBar = (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={city || ALL_VALUE}
        onValueChange={(v) => { setCity(v === ALL_VALUE ? "" : v); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-36 text-sm">
          <SelectValue placeholder="All Cities" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Cities</SelectItem>
          {CITIES.map((c) => (
            <SelectItem key={c} value={c}>{c}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={plan || ALL_VALUE}
        onValueChange={(v) => { setPlan(v === ALL_VALUE ? "" : v); setPage(1); }}
      >
        <SelectTrigger className="h-9 w-40 text-sm">
          <SelectValue placeholder="All Plans" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Plans</SelectItem>
          <SelectItem value="strata">Strata</SelectItem>
          <SelectItem value="property_manager">Property Manager</SelectItem>
          <SelectItem value="individual">Individual</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Buildings</h1>
        <Button size="sm" className="whitespace-nowrap" onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Building
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          <DataTable
            columns={columns as DataTableColumn[]}
            data={(buildingsQuery.data?.data ?? []) as Record<string, unknown>[]}
            loading={buildingsQuery.isLoading}
            totalCount={buildingsQuery.data?.count}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            onSearch={(q) => setSearch(q)}
            searchPlaceholder="Search buildings…"
            filters={filterBar}
            emptyMessage="No buildings found."
          />
        </CardContent>
      </Card>

      {/* Create Building Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Building</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={createForm.handleSubmit((data) => createMutation.mutate(data))}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="cb-name">Building Name <span className="text-red-500">*</span></Label>
              <Input id="cb-name" {...createForm.register("name", { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cb-address">Address <span className="text-red-500">*</span></Label>
              <Input id="cb-address" {...createForm.register("address", { required: true })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cb-city">City</Label>
                <Select
                  value={createForm.watch("city")}
                  onValueChange={(v) => createForm.setValue("city", v)}
                >
                  <SelectTrigger id="cb-city">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cb-plan">Plan <span className="text-red-500">*</span></Label>
                <Select
                  value={createForm.watch("plan")}
                  onValueChange={(v) => createForm.setValue("plan", v)}
                >
                  <SelectTrigger id="cb-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strata">Strata — CA$18.99/mo</SelectItem>
                    <SelectItem value="property_manager">PM — CA$28.99/mo</SelectItem>
                    <SelectItem value="individual">Individual — CA$9.99/mo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cb-nightcap">Night Cap (nights/yr)</Label>
                <Input
                  id="cb-nightcap"
                  type="number"
                  min={1}
                  {...createForm.register("nightCap", { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cb-admin">Admin User ID</Label>
                <Input
                  id="cb-admin"
                  placeholder="UUID"
                  {...createForm.register("adminId")}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="whitespace-nowrap" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating…" : "Create Building"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Building Sheet */}
      <Sheet open={!!editingBuilding} onOpenChange={(open) => { if (!open) setEditingBuilding(null); }}>
        <SheetContent className="w-[400px] sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Edit Building</SheetTitle>
          </SheetHeader>
          <form
            onSubmit={editForm.handleSubmit((data) => {
              if (!editingBuilding) return;
              updateMutation.mutate({
                id: editingBuilding.id,
                data: { name: data.name, address: data.address },
              });
            })}
            className="mt-6 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="eb-name">Building Name <span className="text-red-500">*</span></Label>
              <Input id="eb-name" {...editForm.register("name", { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eb-address">Address</Label>
              <Input id="eb-address" {...editForm.register("address")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eb-nightcap">Night Cap (nights/yr)</Label>
              <Input
                id="eb-nightcap"
                type="number"
                min={1}
                {...editForm.register("nightCap", { valueAsNumber: true })}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditingBuilding(null)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Assign Manager Dialog */}
      <Dialog open={!!assigningBuilding} onOpenChange={(open) => { if (!open) setAssigningBuilding(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign Manager</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Assign a property manager to{" "}
              <span className="font-semibold">{assigningBuilding?.name}</span>.
            </p>
            <div className="space-y-2">
              <Label htmlFor="mgr-id">Manager User ID</Label>
              <Input
                id="mgr-id"
                placeholder="UUID of manager's profile"
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssigningBuilding(null)}>
                Cancel
              </Button>
              <Button
                disabled={!managerId.trim() || assignManagerMutation.isPending}
                onClick={() => {
                  if (!assigningBuilding) return;
                  assignManagerMutation.mutate({
                    buildingId: assigningBuilding.id,
                    mgrId: managerId.trim(),
                  });
                }}
              >
                {assignManagerMutation.isPending ? "Assigning…" : "Assign"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Modal */}
      <ConfirmModal
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        loading={confirmLoading}
        title={
          confirmAction?.type === "delete" ? "Delete Building" : "Suspend Building"
        }
        description={
          confirmAction?.type === "delete"
            ? `Permanently delete "${confirmAction.name}"? All associated units, hosts, and bookings will be removed.`
            : `Suspend "${confirmAction?.name}"? All active hosts and units will be suspended.`
        }
        requireTyping={confirmAction?.type === "delete"}
        variant={confirmAction?.type === "delete" ? "destructive" : "warning"}
      />
    </div>
  );
}
